import * as React from 'react';
import { NextPage } from 'next';
import { Dispatch } from 'redux';
import { useRouter } from 'next/router';
import Head from 'next/head';

import { ProductCollection } from 'components/PrivateMarketplace/ProductCollection/ProductCollection';
import { PrivateMarketplaceLayout } from 'components/PrivateMarketplaceLayout/PrivateMarketplaceLayout';
import { wrapper } from 'ducks';
import {
  fetchCustomTopPageServerSide,
  fetchSettingsWithChatLanguageAdjustmentServerSide,
} from 'ducks/server';
import { fetchProductSummariesServerSide } from 'ducks/server';
import { fetchCustomPagesServerSide } from 'ducks/server';
import { fetchConfigForHostname } from 'lib/util/fetchConfigForHostname';
import { getI18nServerSideProps } from 'i18n';
import { withSSR } from 'withSSR';
import { absoluteUrl } from 'lib/util/absoluetUrl';
import { ReduxState } from 'ducks';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { checkBookingSiteChatVisibility } from 'lib/util/chatChannelVisibility';
import { Chat } from 'components/Chat/Chat';
import { ChatProvider } from 'components/Chat/ChatContext';
import { UrlHistoryProvider } from 'components/Map/useUrlQueryDisplayState';
import { TrackingWebSocketConnectionReceiver } from 'components/TrackingWebSocketConnectionReceiver/TrackingWebSocketConnectionReceiver';
import { ChatLanguageContextSSR } from 'lib/util/chatLanguageDetection';

interface Props {
  apiKey: string;
  host: string;
}

const Category: NextPage<Props> = ({ host }) => {
  const router = useRouter();
  const { i18n } = useTranslation();
  const enableBookingWebsiteChat = useSelector(
    (state: ReduxState) => state.server.settings.all?.chat_settings?.enable_booking_website_chat
  );
  const chatSettings = useSelector((state: ReduxState) => state.server.settings.all?.chat_settings);
  const currentLanguage = i18n.language || 'en-US';

  return (
    <>
      <Head>
        <meta property="og:url" content={`${host}${router.asPath.split('?')[0]}`} />
      </Head>
      <ChatProvider>
        <UrlHistoryProvider>
          <TrackingWebSocketConnectionReceiver />
          <PrivateMarketplaceLayout>
            <ProductCollection host={host} />
          </PrivateMarketplaceLayout>
          <div
            style={
              {
                '--header-height': '64px',
              } as React.CSSProperties
            }
          >
            {checkBookingSiteChatVisibility(
              chatSettings,
              currentLanguage,
              enableBookingWebsiteChat
            ) && <Chat />}
          </div>
        </UrlHistoryProvider>
      </ChatProvider>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(async ({ req, store }) => {
  const hostname = req.headers?.host?.split(':')[0] || '';
  const { apiKey, lang } = await fetchConfigForHostname(hostname);

  const acceptLanguageHeader = Array.isArray(req.headers['accept-language'])
    ? req.headers['accept-language'][0]
    : req.headers['accept-language'];

  const chatContext: ChatLanguageContextSSR = {
    pathname: '/top/[categoryFeatureName]',
    pageLanguage: lang,
    acceptLanguageHeader,
  };

  const dispatch: Dispatch<any> = store.dispatch;
  await Promise.all([
    dispatch(
      fetchSettingsWithChatLanguageAdjustmentServerSide(
        apiKey,
        '',
        lang,
        chatContext,
        `https://${hostname}`
      )
    ),
    dispatch(fetchProductSummariesServerSide(apiKey, lang)),
    dispatch(fetchCustomPagesServerSide(apiKey, lang)),
  ]);

  const isCustomTopPageEnabled = store.getState().server.settings.all?.is_custom_top_page_enabled;

  if (isCustomTopPageEnabled) {
    await dispatch(fetchCustomTopPageServerSide(apiKey, lang));
  }

  const i18nProps = await getI18nServerSideProps(lang);

  (req as any).locale = lang;

  return {
    props: {
      apiKey,
      host: absoluteUrl(req),
      ...i18nProps,
    },
  };
});

export default withSSR()(Category);
